import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useEffect, useRef, useState } from "react";
import "./map.less";

export interface MapProps {
  center?: {
    lat: number;
    lng: number;
  };
  adjPropertiesLoc?: Array<{ lat: number; lng: number; label: string }>;
}

const mapStyle: google.maps.MapTypeStyle[] = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

// Custom REIT marker icon
const createREITMarkerIcon = (): google.maps.Icon => {
  const svgIcon = `
    <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
      <!-- Marker pin -->
      <path d="M16 0C7.163 0 0 7.163 0 16c0 4.5 2.5 8.5 6 11L16 40l10-13c3.5-2.5 6-6.5 6-11C32 7.163 24.837 0 16 0z" 
            fill="#132c49" 
            stroke="white" 
            stroke-width="2"/>
      
      <!-- Building icon inside -->
      <g transform="translate(8, 6)" fill="white">
        <rect x="2" y="8" width="12" height="8" />
        <rect x="2" y="4" width="4" height="4" />
        <rect x="6" y="6" width="2" height="2" />
        <rect x="10" y="4" width="4" height="4" />
        <rect x="8" y="6" width="2" height="2" />
        <rect x="12" y="6" width="2" height="2" />
        <!-- Small windows -->
        <rect x="3" y="10" width="1" height="1" fill="#132c49"/>
        <rect x="5" y="10" width="1" height="1" fill="#132c49"/>
        <rect x="9" y="10" width="1" height="1" fill="#132c49"/>
        <rect x="11" y="10" width="1" height="1" fill="#132c49"/>
        <rect x="13" y="10" width="1" height="1" fill="#132c49"/>
      </g>
      
      <!-- REIT indicator -->
      <circle cx="26" cy="6" r="4" fill="#2fbeb7" stroke="white" stroke-width="1"/>
      <text x="26" y="8" text-anchor="middle" font-size="6" font-weight="bold" fill="white">R</text>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgIcon)}`,
    scaledSize: new google.maps.Size(32, 40),
    anchor: new google.maps.Point(16, 40),
  };
};

// Create styled info window content
const createInfoWindowContent = (label: string): string => {
  return `
    <div class="reit-info-window">
      <div class="reit-info-window__header">
        <div class="reit-info-window__icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#2fbeb7">
            <path d="M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3z"/>
          </svg>
        </div>
        <h3 class="reit-info-window__title">${label}</h3>
      </div>
      <div class="reit-info-window__body">
        <p class="reit-info-window__description">
          Real Estate Investment Trust Property
        </p>
        <div class="reit-info-window__badge">
          REIT Property
        </div>
      </div>
    </div>
  `;
};

const MapComponent: React.FC<MapProps> = ({ center, adjPropertiesLoc }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [markerCluster, setMarkerCluster] = useState<MarkerClusterer>();

  useEffect(() => {
    if (!ref.current || !adjPropertiesLoc?.length) return;

    if (!map) {
      const newMap = new google.maps.Map(ref.current, {
        center: center,
        zoom: 5,
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeControl: false,
        styles: mapStyle,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_BOTTOM
        },
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.RIGHT_TOP
        }
      });
      setMap(newMap);

      const newMarkerCluster = new MarkerClusterer({ 
        map: newMap, 
        markers: [],
        renderer: {
          render: ({ count, position }) => {
            const svg = `
              <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                <circle cx="25" cy="25" r="23" fill="#132c49" stroke="white" stroke-width="3"/>
                <circle cx="25" cy="25" r="15" fill="#2fbeb7"/>
                <text x="25" y="30" text-anchor="middle" font-size="14" font-weight="bold" fill="white">
                  ${count}
                </text>
              </svg>
            `;
            
            return new google.maps.Marker({
              position,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
                scaledSize: new google.maps.Size(50, 50),
                anchor: new google.maps.Point(25, 25)
              }
            });
          }
        }
      });
      setMarkerCluster(newMarkerCluster);
    }

    if (markerCluster && map) {
      markerCluster.clearMarkers();

      adjPropertiesLoc.forEach((loc) => {
        const marker = new google.maps.Marker({
          position: { lat: loc.lat, lng: loc.lng },
          title: loc.label,
          icon: createREITMarkerIcon(),
          animation: google.maps.Animation.DROP,
        });

        const infoWindow = new google.maps.InfoWindow({
          content: createInfoWindowContent(loc.label),
          maxWidth: 300,
        });

        marker.addListener("click", () => {
          infoWindow.open(map, marker);
        });

        // Store info window reference for cleanup
        (marker as any).infoWindow = infoWindow;

        // Add hover effects
        marker.addListener("mouseover", () => {
          marker.setAnimation(google.maps.Animation.BOUNCE);
          setTimeout(() => marker.setAnimation(null), 700);
        });

        markerCluster.addMarker(marker);
      });
    }
  }, [adjPropertiesLoc, center, map, markerCluster]);

  return (
    <div className="reit-map-container">
      <div ref={ref} className="reit-map" />
    </div>
  );
};

export default MapComponent;